// Constants of the "Zásilky" module: statuses, directions, carriers, review reasons, event labels.
// Keys must match docs in docs/SHIPMENTS-API.md (Vocabulary section). Czech UI texts, English code.

// Status colours follow the ERP badge palette (constants/index.js); text colours are darkened to keep 4.5:1 contrast.
export const SHIPMENT_STATUSES = [
  { value: 'objednano', label: 'Objednáno', bg: '#E3F2FD', color: '#1565C0' },
  { value: 'odeslano', label: 'Odesláno', bg: '#E8EAF6', color: '#283593' },
  { value: 'v_preprave', label: 'V přepravě', bg: '#FFF3E0', color: '#BF360C' },
  { value: 'k_vyzvednuti', label: 'K vyzvednutí', bg: '#F3E5F5', color: '#6A1B9A' },
  { value: 'doruceno', label: 'Doručeno', bg: '#E8F5E9', color: '#1B5E20' },
  { value: 'problem', label: 'Problém', bg: '#FFEBEE', color: '#C62828' },
  { value: 'vraceno', label: 'Vráceno', bg: '#EFEFEF', color: '#555555' },
];

// A shipment without any applied event has status null and is shown as "Ke kontrole".
export const REVIEW_STATUS = { value: 'review', label: 'Ke kontrole', bg: '#FFF8E1', color: '#7A5C00' };

export const TERMINAL_STATUSES = ['doruceno', 'vraceno'];

const STATUS_BY_VALUE = Object.fromEntries(SHIPMENT_STATUSES.map((s) => [s.value, s]));

export function statusMeta(status) {
  if (status == null) return REVIEW_STATUS;
  return STATUS_BY_VALUE[status] || { value: String(status), label: String(status), bg: '#EFEFEF', color: '#555555' };
}

export const DIRECTIONS = [
  { value: 'prichozi', label: 'Příchozí' },
  { value: 'odchozi', label: 'Odchozí' },
  { value: 'neurceno', label: 'Neurčeno' },
];

export const DIRECTION_LABELS = Object.fromEntries(DIRECTIONS.map((d) => [d.value, d.label]));

// Fallback names; the filter and the forms use the live list from GET /carriers when it is available.
export const CARRIER_LABELS = {
  zasilkovna: 'Zásilkovna', ppl: 'PPL', dpd: 'DPD', gls: 'GLS', ceska_posta: 'Česká pošta', ups: 'UPS',
  fedex: 'FedEx', dhl: 'DHL', toptrans: 'TOPTRANS', geis: 'Geis', inpost: 'InPost',
  slovenska_posta: 'Slovenská pošta', one_wedo: 'ONE (WeDo)', alza: 'Alza', other: 'Jiný dopravce', unknown: 'Neznámý dopravce',
};

export const CARRIER_CODES = Object.keys(CARRIER_LABELS);

export function carrierLabel(code, name) {
  return name || CARRIER_LABELS[code] || String(code || 'Neznámý dopravce');
}

export const REVIEW_REASONS = {
  low_confidence: 'Nízká jistota AI',
  unverified_tracking: 'Neověřené číslo zásilky',
  carrier_conflict: 'Nesoulad dopravce',
  status_disagreement: 'Stav neodpovídá textu',
  unknown_carrier_with_tracking: 'Neznámý dopravce',
  untrusted_sender: 'Neověřený odesílatel',
  terminal_from_untrusted: 'Doručení z neověřeného zdroje',
  terminal_without_tracking: 'Doručeno bez čísla zásilky',
  injection_suspected: 'Podezření na podvržený obsah',
  ai_refused: 'AI odmítla zpracovat',
  ai_unreadable: 'AI vrátila nečitelnou odpověď',
  ambiguous_shipment: 'Více možných zásilek',
  unlinked: 'Nepřiřazený e-mail',
  party_unknown: 'Neznámý obchod nebo odesílatel',
  shipment_deleted: 'Zásilka byla smazána',
  ai_error: 'Chyba zpracování AI',
};

// Reasons that point at a possibly forged or hostile e-mail get the red chip.
export const DANGEROUS_REASONS = ['injection_suspected', 'terminal_from_untrusted', 'untrusted_sender', 'ai_refused'];

export const reasonLabel = (key) => REVIEW_REASONS[key] || String(key);

export const EVENT_SOURCES = { email: 'E-mail', manual: 'Ručně', system: 'Systém' };

export const APPLIED_REASONS = {
  pending_review: 'Čeká na schválení v kontrole',
  manual_lock: 'Stav je ručně zamčený',
  terminal: 'Zásilka je už v koncovém stavu',
  older_than_state: 'Starší než aktuální stav',
  rejected: 'Zamítnuto při kontrole',
};

export const TRUST_LEVELS = {
  trusted_carrier: 'Ověřený dopravce',
  known_party: 'Známý partner',
  untrusted: 'Neověřený odesílatel',
};

export const REVIEW_STATES = { open: 'Ke kontrole', approved: 'Schváleno', rejected: 'Zamítnuto', auto: 'Zpracováno automaticky' };

// emailType values are produced by the AI classifier (services/shipments/ai/constants.js EMAIL_TYPES);
// unknown values are shown as plain text.
export const EMAIL_TYPES = {
  order_confirmation: 'Potvrzení objednávky',
  shipped: 'Oznámení o odeslání',
  in_transit: 'Zásilka je v přepravě',
  out_for_delivery_or_pickup: 'Na doručení nebo k vyzvednutí',
  delivered: 'Oznámení o doručení',
  problem_or_delay: 'Problém nebo zpoždění',
  returned: 'Vráceno odesílateli',
  irrelevant: 'Nesouvisející e-mail',
};

export const emailTypeLabel = (type) => EMAIL_TYPES[type] || String(type || 'E-mail');

export const LIST_TABS = [
  { value: 'active', label: 'Aktivní' },
  { value: 'review', label: 'Ke kontrole' },
  { value: 'delivered', label: 'Doručené' },
  { value: 'all', label: 'Vše' },
  { value: 'deleted', label: 'Smazané' }, // soft-deleted shipments (rows carry deletedAt); never counted in a badge
];

export const SORT_OPTIONS = [
  { value: 'lastEvent', label: 'Poslední událost' },
  { value: 'created', label: 'Vytvořeno' },
  { value: 'expected', label: 'Očekávané doručení' },
  { value: 'status', label: 'Stav' },
  { value: 'carrier', label: 'Dopravce' },
];

// Summary chips on the list page. `filters` is merged into the URL filters when a chip is chosen.
// The contract has no "expected today" filter, so that chip sorts the active list by the expected date.
export const SUMMARY_CHIPS = [
  { key: 'active', countKey: 'active', label: 'Aktivní', filters: { tab: 'active', highlight: '', sort: '', order: '' } },
  { key: 'expectedToday', countKey: 'expectedToday', label: 'Očekávané dnes', filters: { tab: 'active', highlight: '', sort: 'expected', order: 'asc' } },
  { key: 'delayed', countKey: 'delayed', label: 'Zpožděné', filters: { tab: 'active', highlight: 'delayed', sort: '', order: '' } },
  { key: 'problem', countKey: 'problem', label: 'Problémové', filters: { tab: 'active', highlight: 'problem', sort: '', order: '' } },
  { key: 'stuck', countKey: 'stuck', label: 'Dlouho nehnuté', filters: { tab: 'active', highlight: 'stuck', sort: '', order: '' } },
  { key: 'awaitingTracking', countKey: 'awaitingTracking', label: 'Čeká na tracking', filters: { tab: 'active', highlight: 'awaitingTracking', sort: '', order: '' } },
];

export const HIGHLIGHT_LABELS = { delayed: 'Zpožděné', problem: 'Problémové', stuck: 'Dlouho nehnuté', awaitingTracking: 'Čeká na tracking' };

// Names of locked fields (manualFields) shown in the detail.
export const FIELD_LABELS = {
  carrierCode: 'Dopravce', direction: 'Směr', status: 'Stav', orderRef: 'Číslo objednávky', invoiceRef: 'Faktura',
  description: 'Popis zboží', senderName: 'Odesílatel', recipientName: 'Příjemce', pickupPoint: 'Výdejní místo',
  codAmount: 'Dobírka', codCurrency: 'Měna dobírky', expectedDelivery: 'Očekávané doručení', deliveredAt: 'Doručeno',
  customerId: 'Zákazník', supplierId: 'Dodavatel', machineId: 'Trenažér', orderBatchId: 'Objednávka',
};

export const NOTIFICATION_KINDS = {
  delivered: 'Doručeno', problem: 'Problém', delayed: 'Zpoždění', stuck: 'Dlouho bez pohybu', worker_down: 'Výpadek synchronizace',
};

export const COD_CURRENCIES = ['CZK', 'EUR', 'PLN', 'USD'];

export const PAGE_SIZE = 25;

export const TABPANEL_ID = 'shp-tabpanel';
